import { amountToNumber, newAmount } from '@orus.eu/amount'
import { TechnicalError } from '@orus.eu/error'

import type { PolicyItem } from '../../model/policy-item.ts'

import {
  isNlRcphSegmentWith50kCyberLimit,
  nlRcphCyberCourtProcedureLimitText,
} from './displayed-information/nl-rcph-guarantee-limits.ts'
import type { NlRcphCyberChildGuarantee } from './displayed-information/optional/nl-rcph-displayed-optional-guarantee.ts'
import type { NlRcphActivityConfiguration, NlRcphSegment } from './nl-rcph-activity-configuration.ts'
import { formatNlRcphAmountFromNumber } from './nl-rcph-amount-format.ts'
import type { NlRcphResolvedActivity } from './nl-rcph-archi-activities.ts'
import {
  getNlRcphSegmentFromActivitiesConfigurations,
  hasNlRcphFixedDoubledAnnualLimit,
} from './nl-rcph-policy-item-availability.ts'
import { getNlRcphAllowedPiDeductibles, getNlRcphDoubledAnnualLimit } from './nl-rcph-segment-limit-configuration.ts'

export const NL_RCPH_PI_DEDUCTIBLE_AMOUNTS = [newAmount(0), newAmount(1_000)] as const

export const NL_RCPH_GL_DEDUCTIBLE_AMOUNT = 100
export const NL_RCPH_GL_DEDUCTIBLE_AMOUNT_ARCHI = 150
export const NL_RCPH_GL_DEDUCTIBLE_AMOUNT_SECU = 250
export const NL_RCPH_GL_PER_CLAIM_LIMIT = 2_500_000
export const NL_RCPH_GL_ANNUAL_LIMIT = 5_000_000
export const nlRcphGlFixedDeductible = newAmount(NL_RCPH_GL_DEDUCTIBLE_AMOUNT)

export function getNlRcphGlDeductibleAmount(segment: NlRcphSegment | undefined): number {
  if (segment === 'SECU') {
    return NL_RCPH_GL_DEDUCTIBLE_AMOUNT_SECU
  }

  if (segment === 'ARCHI') {
    return NL_RCPH_GL_DEDUCTIBLE_AMOUNT_ARCHI
  }

  return NL_RCPH_GL_DEDUCTIBLE_AMOUNT
}

export const NL_RCPH_CYBER_LIMIT_AMOUNT = 100_000
export const NL_RCPH_CYBER_LIMIT_AMOUNT_REDUCED = 50_000
export const NL_RCPH_CYBER_FRAUD_LIMIT_AMOUNT = 25_000
export const NL_RCPH_CYBER_INCIDENT_INTERVENTION_LIMIT = 15_000
export const NL_RCPH_CYBER_DEDUCTIBLE_AMOUNT = 1_000
export const nlRcphCyberFixedLimit = newAmount(NL_RCPH_CYBER_LIMIT_AMOUNT)
export const nlRcphCyberFixedDeductible = newAmount(NL_RCPH_CYBER_DEDUCTIBLE_AMOUNT)

export function getNlRcphCyberLimitAmountForSegment(segment: NlRcphSegment | undefined): number {
  return isNlRcphSegmentWith50kCyberLimit(segment) ? NL_RCPH_CYBER_LIMIT_AMOUNT_REDUCED : NL_RCPH_CYBER_LIMIT_AMOUNT
}

export function getNlRcphCyberFraudLimitAmountForSegment(_segment: NlRcphSegment | undefined): number {
  return NL_RCPH_CYBER_FRAUD_LIMIT_AMOUNT
}

export function getNlRcphPiPossibleDeductibleAmountsForSegment(
  segment: NlRcphSegment | undefined,
  activities: readonly NlRcphResolvedActivity[],
): readonly (typeof NL_RCPH_PI_DEDUCTIBLE_AMOUNTS)[number][] {
  if (segment == undefined) {
    return NL_RCPH_PI_DEDUCTIBLE_AMOUNTS
  }

  return getNlRcphAllowedPiDeductibles(segment, activities).map((deductible) => newAmount(deductible))
}

export function buildNlRcphPiPolicyItem({
  perClaimLimit,
  segment,
  activities,
  deductible,
}: {
  perClaimLimit: number | undefined
  segment: NlRcphSegment | undefined
  activities: readonly NlRcphResolvedActivity[]
  deductible?: number
}): PolicyItem {
  const allowedDeductibles = segment == undefined ? [0, 1_000] : getNlRcphAllowedPiDeductibles(segment, activities)
  const deductibleAmount = deductible ?? (allowedDeductibles.includes(0) ? 0 : (allowedDeductibles[0] ?? 0))

  return {
    optionality: { type: 'included' },
    limitOfIndemnity:
      perClaimLimit != undefined ? { type: 'amounts', value: newAmount(perClaimLimit) } : { type: 'text', value: '' },
    deductible: { type: 'amounts', value: newAmount(deductibleAmount) },
  }
}

export function buildNlRcphGlPolicyItem({
  optionality,
  segment,
}: {
  optionality: { type: 'included' } | { type: 'optional'; isActive: boolean }
  segment?: NlRcphSegment | undefined
}): PolicyItem {
  return {
    optionality,
    limitOfIndemnity: {
      type: 'perClaimAndAnnualAmounts',
      perClaimLimit: newAmount(NL_RCPH_GL_PER_CLAIM_LIMIT),
      annualLimit: newAmount(NL_RCPH_GL_ANNUAL_LIMIT),
    },
    deductible: { type: 'fixedAmount', value: newAmount(getNlRcphGlDeductibleAmount(segment)) },
  }
}

export function buildNlRcphCyberLimitOfIndemnity(
  activitiesConfigurations: readonly NlRcphActivityConfiguration[] | null | undefined,
): Extract<PolicyItem['limitOfIndemnity'], { type: 'perClaimAndAnnualAmounts' }> {
  const segment = getNlRcphSegmentFromActivitiesConfigurations(activitiesConfigurations)
  const limitAmount = getNlRcphCyberLimitAmountForSegment(segment)

  return {
    type: 'perClaimAndAnnualAmounts',
    perClaimLimit: newAmount(limitAmount),
    annualLimit: newAmount(limitAmount),
  }
}

export function getNlRcphPolicyItemPerClaimAndAnnualAmounts(policyItem: PolicyItem | null | undefined): {
  perClaimLimit: number
  annualLimit: number
} {
  if (policyItem?.limitOfIndemnity.type !== 'perClaimAndAnnualAmounts') {
    throw new TechnicalError('NL-RCPH policy item limit should be perClaimAndAnnualAmounts', {
      context: { limitOfIndemnityType: policyItem?.limitOfIndemnity.type },
    })
  }

  return {
    perClaimLimit: amountToNumber(policyItem.limitOfIndemnity.perClaimLimit),
    annualLimit: amountToNumber(policyItem.limitOfIndemnity.annualLimit),
  }
}

export function buildNlRcphPerClaimAndAnnualGuaranteeLimits(
  perClaimLimit: number,
  annualLimit: number,
  isGuaranteeFixed = true,
): Array<{
  localizableName: 'nl_rcph_limit_per_claim' | 'nl_rcph_limit_annual'
  limit: string
  isGuaranteeFixed: boolean
}> {
  return [
    {
      localizableName: 'nl_rcph_limit_per_claim',
      limit: formatNlRcphAmountFromNumber(perClaimLimit),
      isGuaranteeFixed,
    },
    {
      localizableName: 'nl_rcph_limit_annual',
      limit: formatNlRcphAmountFromNumber(annualLimit),
      isGuaranteeFixed,
    },
  ]
}

export function buildNlRcphCyberChildGuaranteeLimits(
  child: NlRcphCyberChildGuarantee,
  cyberLimits: { perClaimLimit: number; annualLimit: number },
  segment: NlRcphSegment | undefined,
): Array<{
  localizableName:
    | 'limit_of_indemnity_none'
    | typeof nlRcphCyberCourtProcedureLimitText
    | 'nl_rcph_limit_per_claim'
    | 'nl_rcph_limit_annual'
  limit: string | typeof nlRcphCyberCourtProcedureLimitText | 'limit_of_indemnity_none'
  isGuaranteeFixed: boolean
}> {
  switch (child) {
    case 'nlRcphCyberKeyPerson':
      return []
    case 'nlRcphCyberGeneralOwnDamage':
    case 'nlRcphCyberThirdParty':
      return buildNlRcphPerClaimAndAnnualGuaranteeLimits(cyberLimits.perClaimLimit, cyberLimits.annualLimit)
    case 'nlRcphCyberFraud': {
      const fraudLimit = getNlRcphCyberFraudLimitAmountForSegment(segment)
      return buildNlRcphPerClaimAndAnnualGuaranteeLimits(fraudLimit, fraudLimit)
    }
    case 'nlRcphCyberIncidentIntervention':
      return buildNlRcphPerClaimAndAnnualGuaranteeLimits(
        NL_RCPH_CYBER_INCIDENT_INTERVENTION_LIMIT,
        NL_RCPH_CYBER_INCIDENT_INTERVENTION_LIMIT,
      )
    case 'nlRcphCyberCourtProcedure':
      return [
        {
          localizableName: nlRcphCyberCourtProcedureLimitText,
          limit: nlRcphCyberCourtProcedureLimitText,
          isGuaranteeFixed: true,
        },
      ]
  }
}

export function buildNlRcphCyberPolicyItem({
  optionality,
  activitiesConfigurations,
}: {
  optionality: { type: 'optional'; isActive: boolean }
  activitiesConfigurations: readonly NlRcphActivityConfiguration[] | null | undefined
}): PolicyItem {
  return {
    optionality,
    limitOfIndemnity: buildNlRcphCyberLimitOfIndemnity(activitiesConfigurations),
    deductible: { type: 'fixedAmount', value: nlRcphCyberFixedDeductible },
  }
}

export function getNlRcphPiDerivedAnnualLimit(
  perClaimLimit: number,
  isDoubleLimitActive: boolean,
  segment: NlRcphSegment | undefined,
  activities: readonly NlRcphResolvedActivity[] = [],
): number | undefined {
  if (!isDoubleLimitActive && !hasNlRcphFixedDoubledAnnualLimit(segment, activities, perClaimLimit)) {
    return perClaimLimit
  }

  return getNlRcphDoubledAnnualLimit(perClaimLimit)
}

export function buildNlRcphPiDerivedLimitText(
  perClaimLimit: number,
  annualLimit: number,
): { key: string; parameters: { perClaimAmount: string; annualAmount: string } } {
  return {
    key: 'nl_rcph_pi_derived_limit_of_indemnity_text',
    parameters: {
      perClaimAmount: formatNlRcphAmountFromNumber(perClaimLimit),
      annualAmount: formatNlRcphAmountFromNumber(annualLimit),
    },
  }
}

export function buildNlRcphPiDerivedPolicyItem({
  optionality,
  perClaimLimit,
  annualLimit,
  deductible,
}: {
  optionality: PolicyItem['optionality']
  perClaimLimit: number
  annualLimit: number
  deductible: number
}): PolicyItem {
  return {
    optionality,
    limitOfIndemnity: {
      type: 'perClaimAndAnnualAmounts',
      perClaimLimit: newAmount(perClaimLimit),
      annualLimit: newAmount(annualLimit),
    },
    deductible: { type: 'amounts', value: newAmount(deductible) },
  }
}
